import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import cn from 'classnames';
import moment from 'moment';
import { DateHelper, Popup, LocaleHelper, LocaleManager } from '@bryntum/scheduler';
import { BryntumScheduler } from '@bryntum/scheduler-react';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper.js';
import { getSchedulerConfig } from './schedulerConfig';
import { PL_DialogWrapper } from 'infrastructure/js/components/Dialog/dialog';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import {
  getWeekStartDayIndex,
  getWeekHorizonStart,
  getQuantityInfo,
  getFormattedTokenDetails, getFormattedSetupTime, getCompletedQuantityInfo,
} from '../../../../mat/js/components/Common/Helpers/SchedulerHelper';
import ToolTypeHelper from '../../../../mat/js/utils/toolTypeHelper';
import { formatShiftSegment } from '../../../../mat/js/components/Common/Helpers/ShiftsHelper';
import { getTAskAlertCategoryLabel } from '../../../../mat/js/utils/taskAlertHelper';
import ScheduleAction from '../../../../mat/js/modules/scheduleAction';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import { enumTypes, getEnumValue } from '../../../../mat/js/utils/enumHelper';
import { getSystemColors } from 'infrastructure/js/utils/colorHelper';
import { getPatternStyle } from 'infrastructure/js/components/DynamicIcons/TaskStarted.js';
import ReportTaskProgressDialog from '../../../../mat/js/components/Dialogs/ReportProgressDialog/reportTaskProgressDialog';
import Overlay from 'infrastructure/js/components/Overlay/overlay';
import SchedulerHeader from './Components/SchedulerHeader/schedulerHeader';
import { autoSchedulingStatuses } from '../../../../mat/js/enums/autoSchedulingStatuses';

import './scheduler.scss';

export const schedulerDragData = {
  DRAG_DATA_TASK_ID: 'datatransfertaskid',
  DRAG_DATA_TOOL_ID: 'datatransfertoolid',
};

const oneDayInMilliseconds = 86400000; //1000 * 60 * 60 * 24;

function Scheduler({
  data,
  asyncSchedulingData,
  actions,
  selectedTasks,
  backtrackList,
  refreshPage,
  reportTaskProgressDialogActions,
  setSchedulerEngineInstance,
  onTaskDrop,
  loading,
  sReportTaskProgressDialogData,
}) {
  const labels = useMemo(() => createLabelHelper('mat.scheduler.gantt.'), []);
  const schedulerRef = useRef();
  const schedulerWrapperRef = useRef();
  const refreshTimerRef = useRef();
  const currentTimeIntervalRef = useRef();
  const schedulerConfig = useMemo(() => getSchedulerConfig(), []);
  const weekStartDayIndex = useMemo(() => getWeekStartDayIndex(), []);
  const weekHorizonStart = useMemo(() => getWeekHorizonStart(), []);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [currentKPIsTimeRange, setCurrentKPIsTimeRange] = useState('');
  const asyncSchedulingInProgress = asyncSchedulingData.get('status') === autoSchedulingStatuses.BUSY;

  const showEventInfoPopup = ({ source: scheduler, eventRecord, eventElement }) => {
    const infoPopup =
      scheduler.infoPopup ||
      (scheduler.infoPopup = new Popup({
        cls: 'info-popup',
        closable: true,
        focusOnToFront: false,
      }));

    infoPopup.title = eventRecord.name;
    infoPopup.html = getTooltipTemplate({ eventRecord });
    infoPopup.showBy(eventElement);
  };

  const [eventMenuFeature] = useState({
    processItems: ({ eventRecord, items }) => {
      items.deleteEvent.text = labels.get('task.unschedule');
      items.deleteEvent.disabled = eventRecord.data.locked;

      items.lockUnlock.text = eventRecord.data.locked ? labels.get('task.unlock') : labels.get('task.lock');
      items.lockUnlock.icon = eventRecord.data.locked ? 'b-icon b-fa-unlock' : 'b-icon b-fa-lock';

      if (eventRecord.operationStatus === 'COMPLETED' || eventRecord.operationStatus === 'STARTED') {
        items.deleteEvent.disabled = true;
        items.lockUnlock.disabled = true;
        items.markAsStarted.disabled = true;
        items.startedAsPlanned.disabled = true;
        items.startedAt.disabled = true;
      }

      if (eventRecord.operationStatus === 'COMPLETED') {
        items.markAsCompleted.disabled = true;
        items.completedAsPlanned.disabled = true;
        items.completedAt.disabled = true;
      }
    },
    items: {
      lockUnlock: {
        weight: 100,
        onItem: ({ eventRecord }) => (eventRecord.data.locked ? actions.onTaskUnlock(eventRecord) : actions.onTaskLock(eventRecord)),
      },
      showInfo: {
        text: labels.get('task.showInfo'),
        icon: 'b-fa-circle-info',
        onItem: showEventInfoPopup,
      },
      markAsStarted: {
        cls: 'custom-context-menu-item',
        content: markAsStartedMenuItemRenderer(labels.get('task.markAsStarted')),
        onItem: ({ eventRecord }) =>
          actions.reportTaskProgress({
            taskData: eventRecord.data,
            status: getEnumValue(enumTypes.TASK_STATUS)('STARTED'),
            isReportAsPlanned: false,
          }),
      },
      startedAsPlanned: {
        cls: 'custom-context-menu-item',
        content: markAsStartedMenuItemRenderer(labels.get('task.startedAsPlanned')),
        onItem: ({ eventRecord }) =>
          actions.reportTaskProgress({
            taskData: eventRecord.data,
            status: getEnumValue(enumTypes.TASK_STATUS)('STARTED'),
            isReportAsPlanned: true,
          }),
      },
      startedAt: {
        cls: 'custom-context-menu-item',
        content: markAsStartedMenuItemRenderer(labels.get('task.startedAt')),
        onItem: ({ eventRecord }) =>
          reportTaskProgressDialogActions.show({
            taskData: eventRecord.data,
            status: getEnumValue(enumTypes.TASK_STATUS)('STARTED'),
          }),
      },
      markAsCompleted: {
        cls: 'custom-context-menu-item',
        content: markAsCompletedMenuItemRenderer(labels.get('task.markAsCompleted')),
        onItem: ({ eventRecord }) =>
          actions.reportTaskProgress({
            taskData: eventRecord.data,
            status: getEnumValue(enumTypes.TASK_STATUS)('COMPLETED'),
            isReportAsPlanned: false,
          }),
      },
      completedAsPlanned: {
        cls: 'custom-context-menu-item',
        content: markAsCompletedMenuItemRenderer(labels.get('task.completedAsPlanned')),
        onItem: ({ eventRecord }) =>
          actions.reportTaskProgress({
            taskData: eventRecord.data,
            status: getEnumValue(enumTypes.TASK_STATUS)('COMPLETED'),
            isReportAsPlanned: true,
          }),
      },
      completedAt: {
        cls: 'custom-context-menu-item',
        content: markAsCompletedMenuItemRenderer(labels.get('task.completedAt')),
        onItem: ({ eventRecord }) =>
          reportTaskProgressDialogActions.show({
            taskData: eventRecord.data,
            status: getEnumValue(enumTypes.TASK_STATUS)('COMPLETED'),
          }),
      },
      unassignEvent: false,
      copyEvent: false,
      cutEvent: false,
    },
  });

  const updateKPIsTimeRange = useCallback(() => {
    if (schedulerRef?.current?.instance) {
      let value = DateHelper.floor(schedulerRef.current.instance?.viewportCenterDate, oneDayInMilliseconds);

      let firstDateOfWeek = getFirstDateOfWeek(value, weekStartDayIndex);
      let formattedStart = DateHelper.format(firstDateOfWeek, DateTimeHelper.getDateFormat());

      let lastDateOfWeek = getLastDateOfWeek(value, weekStartDayIndex);
      let formattedEnd = DateHelper.format(lastDateOfWeek, DateTimeHelper.getDateFormat());

      let horizon = labels.get('kpi.horizon.oneWeek');
      let newKPIsTimeRange = `${formattedStart} - ${formattedEnd} ${horizon}`;

      if (__DEV__) {
        let weekNumber = DateHelper.format(value, 'W');
        newKPIsTimeRange += ' (' + weekNumber + ')';
      }

      if (currentKPIsTimeRange !== newKPIsTimeRange) {
        setCurrentKPIsTimeRange(newKPIsTimeRange);
      }
    }
  }, [currentKPIsTimeRange, labels, weekStartDayIndex]);

  // Handle cases where exit fullscreen mode is initiated by the browser's built in buttons / esc key
  const fullScreenChangeHandler = () => {
    const isExitingFullscreen = !document.fullscreenElement;
    if (isExitingFullscreen && document.exitFullscreen) {
      setFullScreenMode(false);
      refreshTimerRef.current?.kill();
    }
  };

  const toggleFullScreen = () => (document.fullscreenElement ? exitFullscreen() : enterFullscreen());

  const reloadPage = () => {
    refreshPage({}, true);
  };

  const enterFullscreen = async () => {
    const ganttRefreshRate = PermissionManager.getOrgPreferences().refreshRateGanttTvMode;
    try {
      await schedulerWrapperRef.current?.requestFullscreen();
      setFullScreenMode(true);
      if (ganttRefreshRate > 0) {
        refreshTimerRef.current = ScheduleAction('gantt refresh timer', reloadPage, ganttRefreshRate);
      }
    } catch (err) {
      console.log('Failed to request gantt fullscreen mode ', err);
    }
  };

  const exitFullscreen = async () => {
    try {
      await document.exitFullscreen();
      setFullScreenMode(false);
      refreshTimerRef.current?.kill();
    } catch (err) {
      console.log('Failed to request gantt exitFullscreen mode ', err);
    }
  };

  const currentTimeLineUpdater = () => {
    const schedulerInstance = schedulerRef.current?.instance;
    if (schedulerInstance) {
      const serverTime = DateTimeHelper.getOrgDateTime();

      schedulerInstance.timeRangeStore.data = [
        {
          name: serverTime.format('HH:mm'),
          startDate: serverTime.format('YYYY-MM-DD HH:mm'),
          cls: 'pl-scheduler-time-range',
        },
      ];
    }
  };

  const clearResourceStoreFilters = ({ source: scheduler }) => {
    scheduler.resourceStore.clearFilters();
  };

  ///////////////////////////////////////////////////////////////////////////
  //--------------------------------- Effects -------------------------------
  useEffect(() => {
    currentTimeIntervalRef.current = setInterval(currentTimeLineUpdater, 60000);
    return () => clearInterval(currentTimeIntervalRef.current);
  }, []);

  useEffect(() => {
    let locale = DateTimeHelper.getUserOrgLocale();

    const applyLocale = (localeName, localeDesc) => {
      let locale = {
        localeName,
        localeDesc,
        DateHelper: {
          locale: localeName,
          weekStartDay: weekStartDayIndex,
        },
        Dependencies: {
          from: labels.get('dependency.from'),
          to: labels.get('dependency.to'),
        },
      };

      LocaleHelper.publishLocale(localeName, { desc: localeDesc, locale });

      LocaleManager.applyLocale(localeName);
    };

    if (locale === 'en') {
      applyLocale('En', 'English');
    } else if (locale === 'de') {
      applyLocale('De', 'German');
    } else if (locale === 'zh-cn') {
      applyLocale('Zh', 'Chinese');
    }

    // detect enter/exit fullscreen mode
    document.addEventListener('fullscreenchange', fullScreenChangeHandler);

    return () => {
      refreshTimerRef.current?.kill();
      document.removeEventListener('fullscreenchange', fullScreenChangeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Do stuff when scheduler data changes
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance;

    if (!scheduler || scheduler.isDestroyed || scheduler.isDestroying) {
      return;
    }

    (async function () {
      scheduler.suspendRefresh();

      const { events = [], resources = [], selectedDependencies = [], resourceTimeRanges = [] } = data;

      scheduler.resourceStore.data = resources;
      scheduler.eventStore.data = events;
      scheduler.dependencyStore.data = selectedDependencies;
      scheduler.resourceTimeRangeStore.data = resourceTimeRanges;

      await scheduler.project.commitAsync();

      // update resource column header
      const newColumnsData = [...scheduler.columns.data];
      newColumnsData[0].totalUtl = data.totalUtl;
      scheduler.columns.data = newColumnsData;
      await scheduler.project.commitAsync();
      scheduler.resumeRefresh(true);

      if(schedulerWrapperRef.current){ // TODO - find better fix for this issue (SAAS-20092)
        schedulerWrapperRef.current.querySelector('.b-timeaxissubgrid').scrollLeft += 1
      }
    })();
  }, [data]);

  // Do stuff when selectedTasks changes
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance;

    if (!scheduler || scheduler.isDestroyed || scheduler.isDestroying) {
      return;
    }

    (async function () {
      scheduler.suspendRefresh();
      scheduler.dependencyStore.data = data.selectedDependencies;
      await scheduler.dependencyStore.project.commitAsync();
      scheduler.resumeRefresh(true);
    })();
  }, [selectedTasks, data?.selectedDependencies]);

  ///////////////////////////////////////////////////
  //------------------- Handlers -----------------

  const onBeforeEventDrag = (event) => {
    if (fullScreenMode || asyncSchedulingInProgress) return false;

    actions.onBeforeTaskMove(event);
    if (_shouldPreventMove(event)) {
      return false;
    }
    const schedulerInstance = schedulerRef?.current?.instance;
    if (schedulerInstance) {
      schedulerInstance.features.eventDrag.constrainDragToResource = event.eventRecord.isLockedForResource;
    }
  };

  const onAfterEventDrop = (event) => {
    const schedulerInstance = schedulerRef?.current?.instance;
    if (schedulerInstance) {
      schedulerInstance.selectedEvents = []; //Remove the selection of the dragged event.
    }

    actions.onTaskMove(event);
  };

  const onSelectionChanged = (event) => {
    let selectedEvents = event.selection.map((selectedEvent) => {
      return selectedEvent.data;
    });
    actions.onSelectionChanged(selectedEvents);
  };

  const onEventClick = (event) => {
    actions.onSelectionChanged([event.eventRecord.data]);
  };

  const isDragDataValid = (e) => {
    return e.dataTransfer.types.includes(schedulerDragData.DRAG_DATA_TASK_ID);
  };

  const onDragEnter = (e) => {
    if (isDragDataValid(e)) {
      e.preventDefault();
    }
  };

  const onDragOver = (e) => {
    if (isDragDataValid(e)) {
      e.preventDefault();
    }
  };

  const onDrop = (e) => {
    if (!isDragDataValid(e)) {
      return;
    }

    let taskId = e.dataTransfer.getData(schedulerDragData.DRAG_DATA_TASK_ID);
    let toolId = e.dataTransfer.getData(schedulerDragData.DRAG_DATA_TOOL_ID) ?? null;

    const schedulerInstance = schedulerRef?.current?.instance;
    if (!schedulerInstance) {
      return;
    }

    let date = schedulerInstance.getDateFromDomEvent(e.nativeEvent, 'round');

    let resource = schedulerInstance.resolveResourceRecord(e.nativeEvent);
    if (!resource || !resource.data || !resource.data.id) {
      return;
    }

    e.preventDefault();

    let momentDate = date ? moment(date, DateTimeHelper.getDateFormat(), DateTimeHelper.getUserOrgLocale(), true).utcOffset(0, true) : null;

    let data = {
      startTime: DateTimeHelper.ConvertFromDate(momentDate),
      resourceId: resource.data.id,
      toolId: toolId ? Number(toolId) : null,
    };

    onTaskDrop(taskId, data);
  };

  const beforePdfExport = ({ config }) => {
    const { rows, client } = config;
    if (rows.length) {
      const ids = rows.map(({ value }) => value);
      client.resourceStore.filter(({ id }) => ids.includes(id));
    }
  };

  const eventMenuBeforeShowHandler = ({items}) => {
    if(fullScreenMode){
      return false;
    }

    if(asyncSchedulingInProgress){
      items.lockUnlock.disabled = true;
      items.deleteEvent.disabled = true;
    }
  }

  //////////////////////////////////////////////////////
  //------------------- Callback Refs -----------------

  const setSchedulerRef = useCallback((r) => {
    schedulerRef.current = r;
    if (schedulerRef.current) {
      currentTimeLineUpdater();

      setSchedulerEngineInstance(schedulerRef.current.schedulerInstance);

      const { schedulerInstance } = schedulerRef.current;
      const scrollOptions = {
        block: 'start',
        edgeOffset: 50, // px.
        animate: false,
      };

      //check in system setting if the WeekHorizonStart setting is not 'today' and check if zoom level is 'week' or 'two weeks'.
      if (weekHorizonStart !== 'today' && [0, 1].includes(schedulerInstance.zoomLevel)) {
        //initialize the scheduler from first day of the current week.
        schedulerInstance.scrollToDate(DateTimeHelper.getDateByWeekDay(weekStartDayIndex, new Date()), scrollOptions);
        return;
      }

      // if WeekHorizonStart is today, scroll to current org time.
      const formattedOrgDate = DateTimeHelper.getOrgDateTime().format('YYYY-MM-DDTHH:mm:ss');
      schedulerInstance.scrollToDate(new Date(formattedOrgDate), scrollOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSchedulerWrapperRef = (r) => {
    schedulerWrapperRef.current = r;
  };

  //////////////////////////////////////////////////////
  //------------------- Render Logic -----------------
  if (!data) {
    return null;
  }

  const getFullscreenModeLoadingOverlay = () => {
    if (loading && fullScreenMode) {
      return <Overlay.Loading />;
    }

    return null;
  };

  return (
    <div ref={setSchedulerWrapperRef} className={cn('pl-scheduler', { 'full-screen-mode': fullScreenMode })}>
      <SchedulerHeader
        schedulerInstance={schedulerRef.current?.instance}
        actions={{ ...actions, updateKPIsTimeRange, toggleFullScreen }}
        backtrackList={backtrackList}
        metrics={data.metrics}
        currentKPIsTimeRange={currentKPIsTimeRange}
        resources={data.resources}
        schedulingStatus={asyncSchedulingData}
      />

      <div className="pl-scheduler-container" onDragEnter={onDragEnter} onDragOver={onDragOver} onDrop={onDrop}>
        <BryntumScheduler
          ref={setSchedulerRef}
          {...schedulerConfig}
          startDate={data.timeDefinition.startDate}
          onBeforeEventDrag={onBeforeEventDrag}
          onAfterEventDrop={onAfterEventDrop}
          onBeforeEventDelete={actions.onTaskDelete}
          onEventSelectionChange={onSelectionChanged}
          onEventClick={onEventClick}
          listeners={{
            horizontalScroll: updateKPIsTimeRange,
            eventMenuBeforeShow: eventMenuBeforeShowHandler,
            beforePdfExport,
            pdfExport: clearResourceStoreFilters,
            eventDblClick: showEventInfoPopup
          }}
          eventMenuFeature={eventMenuFeature}
        />
      </div>

      {getFullscreenModeLoadingOverlay()}

      <PL_DialogWrapper
        dialogComponent={ReportTaskProgressDialog}
        show={sReportTaskProgressDialogData.get('show')}
        actions={reportTaskProgressDialogActions}
        sData={sReportTaskProgressDialogData}
      />
    </div>
  );
}

export default Scheduler;

///////////////////////////////////////////////////////////////////////////////////////////////
//------------------------------------------- Helpers -----------------------------------------
///////////////////////////////////////////////////////////////////////////////////////////////

function _shouldPreventMove(event) {
  if (
    (event.eventRecord && event.eventRecord.locked) ||
    event.eventRecord.operationStatus === 'COMPLETED' ||
    event.eventRecord.operationStatus === 'STARTED'
  ) {
    return true;
  }
}

function getFirstDateOfWeek(dateObject, firstDayOfWeekIndex) {
  const dayOfWeek = dateObject.getDay();
  let firstDayOfWeek = new Date(dateObject);
  let diff = dayOfWeek >= firstDayOfWeekIndex ? dayOfWeek - firstDayOfWeekIndex : 6 - (firstDayOfWeekIndex - dayOfWeek - 1);

  firstDayOfWeek.setDate(dateObject.getDate() - diff);
  firstDayOfWeek.setHours(0, 0, 0, 0);

  return firstDayOfWeek;
}

function getLastDateOfWeek(dateObject, firstDayOfWeekIndex) {
  const dayOfWeek = dateObject.getDay();
  let lastDayOfWeek = new Date(dateObject);
  let diff = dayOfWeek >= firstDayOfWeekIndex ? 6 - dayOfWeek + firstDayOfWeekIndex : firstDayOfWeekIndex - dayOfWeek - 1;

  lastDayOfWeek.setDate(dateObject.getDate() + diff);
  lastDayOfWeek.setHours(0, 0, 0, 0);

  return lastDayOfWeek;
}

function markAsCompletedMenuItemRenderer(text) {
  const { completed } = getSystemColors();
  const icon = `<div class='scheduler-menu-icon' style="background-color:${completed}"></div>`;
  return `<div class='custom-menu-item'>${icon}<span>${text}</span></div>`;
}

function markAsStartedMenuItemRenderer(text) {
  const { started } = getSystemColors();
  const style = getPatternStyle(started).css;
  const icon = `<div class='scheduler-menu-icon' style="${style}"></div>`;
  return `<div class='custom-menu-item'>${icon}<span>${text}</span></div>`;
}

function getTooltipTemplate(data) {
  const labels = createLabelHelper('mat.scheduler.page.');
  const statusLabels = createLabelHelper('enum.planneroperation.status.');

  let schedulerData = data.eventRecord.data;
  let woName = data?.eventRecord?.data?.wo?.name;
  let woDescription = data?.eventRecord?.data?.wo?.description;
  let quantity = getQuantityInfo(data?.eventRecord?.data?.quantity, data?.eventRecord?.data?.workOrderQuantity);
  let kitTypeName = data?.eventRecord?.data?.kitType?.name;
  let kitTypeDescription = data?.eventRecord?.data?.kitType?.description;
  let materialName = data?.eventRecord?.data?.materialName;
  let plannedStartTime = schedulerData.plannedStartDisplayDate;
  let plannedEndTime = schedulerData.plannedEndDisplayDate;
  let actualStartTime = schedulerData.actualStartDisplayDate;
  let actualEndTime = schedulerData.actualEndDisplayDate;
  let estimatedStartTime = schedulerData.estimatedStartDisplayDate;
  let estimatedEndTime = schedulerData.estimatedEndDisplayDate;
  let plannedDuration = schedulerData.taskDuration ? DateTimeHelper.ConvertMinutesToHoursMinutes(schedulerData.taskDuration) : '';
  let actualDuration = schedulerData.actualDuration ? DateTimeHelper.ConvertMinutesToHoursMinutes(schedulerData.actualDuration) : '';
  let plannedResource = schedulerData.plannedResource ? schedulerData.plannedResource.name : '';
  let actualResource = schedulerData.actualResource ? schedulerData.actualResource.name : '';
  let status = schedulerData.operationStatus ? statusLabels.get(schedulerData.operationStatus) : '';
  let priority = schedulerData.priority ? schedulerData.priority : '';
  let recipe = schedulerData.recipe ? schedulerData.recipe : '';
  let chain = schedulerData.vacuumPort ?? '';
  let compatibleToolType = schedulerData.compatibleToolType
    ? ToolTypeHelper.getToolTypeFullLabel(schedulerData.compatibleToolType.name, schedulerData.compatibleToolType.businessId)
    : '';
  let plannedToolId = schedulerData.plannedTool ? schedulerData.plannedTool.businessId : '';
  if (plannedToolId) {
    plannedToolId = plannedToolId + ' ' + getQuantityInfo(data?.eventRecord?.data?.quantity, data?.eventRecord?.data?.toolCapacity);
  }
  let actualToolId = schedulerData.actualTool ? schedulerData.actualTool.businessId : '';
  let splitAssignments = schedulerData?.splitAssignments;
  let tokensDetails = getFormattedTokenDetails(schedulerData?.tokenValue);
  let setupTime = getFormattedSetupTime(schedulerData?.plannedSetupTimeMinutes, schedulerData?.plannedSetupTimePercent);
  let completedQuantity = getCompletedQuantityInfo(schedulerData?.completedQuantity, schedulerData?.quantity);

  return `<div class="event-info-template">
            ${getAlertPart(data)}
            ${getLockedPart(data)}
            ${getSplitTaskPart(data)}
              <div class="tooltip-container">
                <div class="tooltip-section">
                  ${getTooltipSectionIfExists(woName, labels.get('task.tooltip.wo'))}
                  ${getTooltipSectionIfExists(woDescription, labels.get('task.tooltip.woDescription'))}
                  ${getTooltipSectionIfExists(quantity, labels.get('task.tooltip.quantity'))}
                  ${getTooltipSectionIfExists(compatibleToolType, labels.get('task.tooltip.compatibleToolType'))}
                  ${getTooltipSectionIfExists(actualStartTime, labels.get('task.tooltip.startTime'))}
                  ${getTooltipSectionIfExists(actualEndTime, labels.get('task.tooltip.endTime'))}
                  ${getTooltipSectionIfExists(estimatedStartTime, labels.get('task.tooltip.estimatedStartTime'))}
                  ${getTooltipSectionIfExists(estimatedEndTime, labels.get('task.tooltip.estimatedEndTime'))}
                  ${getTooltipSectionIfExists(actualDuration, labels.get('task.tooltip.duration'))}
                  ${getTooltipSectionIfExists(actualResource, labels.get('task.tooltip.actualResource'))}
                  ${getTooltipSectionIfExists(status, labels.get('task.tooltip.status'))}
                  ${getTooltipSectionIfExists(priority, labels.get('task.tooltip.priority'))}
                  ${getTooltipSectionIfExists(recipe, labels.get('task.tooltip.recipe'))}
                  ${getTooltipSectionIfExists(chain, labels.get('task.tooltip.chain'))}
                  ${getTooltipSectionIfExists(materialName, labels.get('task.tooltip.material'))}
                  ${
                    PermissionManager.getOrgPreferences().schedulerHumanCapacityEnabled
                      ? getTooltipSectionShifts(splitAssignments, labels.get('task.tooltip.shifts'))
                      : ''
                  }
                 </div>

                 <div class="tooltip-section">
                   ${getTooltipSectionIfExists(kitTypeName, labels.get('task.tooltip.kitType'))}
                   ${getTooltipSectionIfExists(kitTypeDescription, labels.get('task.tooltip.kitTypeDescription'))}
                   ${getTooltipSectionIfExists(plannedStartTime, labels.get('task.tooltip.plannedStartTime'))}
                   ${getTooltipSectionIfExists(plannedEndTime, labels.get('task.tooltip.plannedEndTime'))}
                   ${getTooltipSectionIfExists(plannedDuration, labels.get('task.tooltip.plannedDuration'))}
                   ${getTooltipSectionIfExists(plannedResource, labels.get('task.tooltip.plannedResource'))}
                   ${getTooltipSectionIfExists(plannedToolId, labels.get('task.tooltip.plannedToolId'))}
                   ${getTooltipSectionIfExists(actualToolId, labels.get('task.tooltip.toolId'))}
                   ${getTooltipSectionIfExists(tokensDetails, labels.get('task.tooltip.tokensDetails'))}
                   ${getTooltipSectionIfExists(setupTime, labels.get('task.tooltip.setupTime'))}
                   ${getTooltipSectionIfExists(completedQuantity, labels.get('task.tooltip.completedQuantity'))}
                 </div>
              </div>
          </div>`;
}

function getAlertPart(data) {
  const labels = createLabelHelper('mat.scheduler.page.');

  let alerts = data?.eventRecord?.data?.validationMessages;
  if (!alerts || !alerts.length) {
    return '';
  }

  let alertsHigh = alerts.filter((a) => {
    return a.severity === 'HIGH';
  });
  let alertsLow = alerts.filter((a) => {
    return a.severity !== 'HIGH';
  });

  let alertsHighSummary = alertsHigh?.length
    ? `<div><span class="pl pl-error-icon"><label>${labels.get('task.tooltip.alert.highLevel', undefined, {
        amount: alertsHigh.length,
      })}</label></span></div>`
    : '';
  let alertsLowSummary = alertsLow?.length
    ? `<div><span class="pl pl-warning-icon"><label>${labels.get('task.tooltip.alert.lowLevel', undefined, {
        amount: alertsLow.length,
      })}</label></span></div>`
    : '';

  let alertsHighDetails = alertsHigh?.length ? getAlertsBySeverity(alertsHigh, 'HIGH') : '';
  let alertsLowDetails = alertsLow?.length ? getAlertsBySeverity(alertsLow, 'LOW') : '';

  return `<div class="alert-section">
              <div id="eventTooltipAlertsSummary">
                  <button onclick="
                    const alertDetailsElement = document.getElementById('eventTooltipAlertsDetails');
                    const alertSummaryElement = document.getElementById('eventTooltipAlertsSummary');
                    const alertSummaryBodyElement = alertSummaryElement.getElementsByClassName('alert-summary-body')[0];
                    const arrowUp = alertSummaryElement.getElementsByClassName('pl-arrow-up')[0];
                    const arrowDown = alertSummaryElement.getElementsByClassName('pl-arrow-down')[0];

                    const isShown = alertDetailsElement.dataset.visible === 'false' ? true : false;
                    alertSummaryElement.classList.toggle('summary-collapsed');
                    alertDetailsElement.dataset.visible = isShown.toString();
                    alertDetailsElement.style.display = isShown ? 'block' : 'none';
                    arrowUp.style.display = isShown ? 'block' : 'none';
                    arrowDown.style.display = isShown ? 'none' : 'block';
                    alertSummaryBodyElement.style.display = isShown ? 'none' : 'block';
                  ">
                    <span class="pl pl-arrow-down"></span>
                    <span class="pl pl-arrow-up" style="display: none;"></span>
                  </button>
                  <div class="alert-summary-body" data-visible="true">
                    ${alertsHighSummary}
                    ${alertsLowSummary}
                  </div>
              </div>
              <div id="eventTooltipAlertsDetails" data-visible="false">
                  ${alertsHighDetails}
                  ${alertsLowDetails}
              </div>
            </div>`;
}

function getLockedPart(data) {
  const labels = createLabelHelper('mat.scheduler.page.');
  let isLocked = data?.eventRecord?.data?.locked;
  if (!isLocked) {
    return '';
  }
  return `<div class="lock-section"><span class="pl pl-square-regular"></span>${labels.get('task.tooltip.locked')}</div>`;
}

function getAlertsBySeverity(alerts, severity) {
  const labels = createLabelHelper('');

  if (alerts && alerts.length > 0) {
    let messages = alerts.map((alert) => {
      return `<div>
      <span class="category">${getTAskAlertCategoryLabel(alert.category) + ':'}</span>
      <span>${labels.get(alert.id, undefined, alert.params ? alert.params : undefined)}</span>
    </div>`;
    });

    let icon = severity === 'HIGH' ? 'alert-icon pl pl-error-icon error' : 'alert-icon pl pl-warning-icon warning';
    return `<div>
              <div class="${icon}"></div>
              ${messages.join('')}
            </div>`;
  }
}

function getSplitTaskPart(data) {
  const labels = createLabelHelper('mat.scheduler.page.');

  let isSplit = data?.eventRecord?.data?.split;
  if (isSplit) {
    return `<div class="split-task-section"><span class="pl pl-split-task-icon"></span>${labels.get('task.tooltip.splitTask')}</div>`;
  }
  return '';
}

function getTooltipSectionIfExists(value, title) {
  if (!value) {
    return '';
  }
  return `<dt>${title}</dt>
                <dd title="${value}">${value}</dd>`;
}

function getTooltipSectionShifts(shiftSegments, title) {
  if (!shiftSegments || !shiftSegments.length) {
    return '';
  }

  const shiftSegmentsElements = shiftSegments.map((segment) => `<dd>${formatShiftSegment(segment, true)}</dd>`).join('');
  return `<dt>${title}</dt> ${shiftSegmentsElements}`;
}
